import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import ApiService from '../../services/api';

/**
 * AI SUMMARY:
 * The CreateFund component provides a form for users to create a new fund.
 * Features:
 * - Form with validation for required fields
 * - Error handling and display
 * - Loading state management during API calls
 * - Navigation back to fund list on success or cancel
 *
 * The component submits the form data to the API and redirects to the
 * fund list page upon successful creation.
 */
const CreateFund = () => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      setError('Fund name is required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('apiToken');
      if (!token) {
        setError('No API token found');
        setLoading(false);
        return;
      }

      const apiService = new ApiService(token);
      await apiService.createFund({
        name,
        description: description.trim() ? description : undefined,
      });

      // Navigate to fund list on success
      navigate('/fund/list');
    } catch (error) {
      console.error('Error creating fund:', error);
      setError('Failed to create fund');
      setLoading(false);
    }
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Create New Fund</h1>
        <Link
          to="/fund/list"
          className="bg-gray-200 px-4 py-2 rounded hover:bg-gray-300"
        >
          Back to Funds
        </Link>
      </div>

      <div className="bg-white p-6 rounded shadow">
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label
              className="block text-gray-700 text-sm font-bold mb-2"
              htmlFor="name"
            >
              Fund Name *
            </label>
            <input
              id="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              placeholder="Enter fund name"
              required
            />
          </div>

          <div className="mb-6">
            <label
              className="block text-gray-700 text-sm font-bold mb-2"
              htmlFor="description"
            >
              Description
            </label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              placeholder="Enter fund description"
              rows={4}
            />
          </div>

          <div className="flex items-center justify-end">
            <button
              type="button"
              className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded mr-2"
              onClick={() => navigate('/fund/list')}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
              disabled={loading}
            >
              {loading ? 'Creating...' : 'Create Fund'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateFund;
